'use client';

import * as React from 'react';
import Masonry from 'react-masonry-css';
import { Checkbox } from '@heroui/react';
import { trpc } from '@/lib/trpc';
import {
  checkerboardStyle,
  GALLERY_PAGE_SIZE,
  masonryColumnsForContainerWidth,
} from './constants';
import type { AssetMediaItem, AssetScope } from './types';

export type AssetImageMasonryPanelProps = {
  active: boolean;
  listHostOpen: boolean;
  scope: AssetScope;
  searchKeyword: string;
  selectionMode: boolean;
  selectedIds: ReadonlySet<string>;
  onToggleSelect: (item: AssetMediaItem) => void;
  scrollRootRef?: React.RefObject<HTMLElement | null>;
};

function buildListInput(page: number, searchKeyword: string) {
  const kw = searchKeyword.trim();
  const nameFilter = kw ? { name: { $like: `%${kw}%` } } : undefined;

  return {
    page,
    pageSize: GALLERY_PAGE_SIZE,
    type: 'image' as const,
    ...(nameFilter ? { where: nameFilter } : {}),
  };
}

export function AssetImageMasonryPanel({
  active,
  listHostOpen,
  scope,
  searchKeyword,
  selectionMode,
  selectedIds,
  onToggleSelect,
  scrollRootRef,
}: AssetImageMasonryPanelProps) {
  const [page, setPage] = React.useState(1);
  const [accumulated, setAccumulated] = React.useState<AssetMediaItem[]>([]);
  const loadMoreSentinelRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  const [internalScrollEl, setInternalScrollEl] = React.useState<HTMLDivElement | null>(null);
  const loadMoreGateRef = React.useRef({ hasNext: false, isFetching: false });
  const [masonryColumnCount, setMasonryColumnCount] = React.useState(4);
  const [loadedImageIds, setLoadedImageIds] = React.useState(() => new Set<string>());
  const prevListHostOpenRef = React.useRef(false);
  const prevScopeRef = React.useRef(scope);
  const prevSearchRef = React.useRef(searchKeyword);

  const resetList = React.useCallback(() => {
    setPage(1);
    setAccumulated([]);
    setLoadedImageIds(new Set());
  }, []);

  React.useEffect(() => {
    if (listHostOpen && !prevListHostOpenRef.current) {
      resetList();
    }
    prevListHostOpenRef.current = listHostOpen;
  }, [listHostOpen, resetList]);

  React.useEffect(() => {
    if (prevScopeRef.current !== scope || prevSearchRef.current !== searchKeyword) {
      resetList();
      prevScopeRef.current = scope;
      prevSearchRef.current = searchKeyword;
    }
  }, [scope, searchKeyword, resetList]);

  React.useLayoutEffect(() => {
    if (!active) return;
    const el = measureRef.current;
    if (!el) return;
    let rafId: number | null = null;
    const update = () => {
      setMasonryColumnCount(masonryColumnsForContainerWidth(el.clientWidth));
    };
    update();
    rafId = window.requestAnimationFrame(update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [active]);

  const listInput = React.useMemo(
    () => buildListInput(page, searchKeyword),
    [page, searchKeyword],
  );

  const listMyQuery = trpc.assets.listMy.useQuery(listInput, {
    enabled: active && scope === 'my',
  });
  const listSpaceQuery = trpc.assets.listSpaceAssets.useQuery(listInput, {
    enabled: active && scope === 'space',
  });
  const listSystemQuery = trpc.assets.listSystemAssets.useQuery(listInput, {
    enabled: active && scope === 'system',
  });

  const query =
    scope === 'my' ? listMyQuery : scope === 'space' ? listSpaceQuery : listSystemQuery;

  React.useEffect(() => {
    const batch = (query.data?.items as AssetMediaItem[] | undefined) ?? [];
    if (page === 1) {
      setAccumulated(batch);
      return;
    }
    setAccumulated((prev) => {
      const seen = new Set(prev.map((i) => String(i.id)));
      const next = [...prev];
      for (const it of batch) {
        const key = String(it.id);
        if (seen.has(key)) continue;
        seen.add(key);
        next.push(it);
      }
      return next;
    });
  }, [page, query.data]);

  React.useEffect(() => {
    setLoadedImageIds((prev) => {
      if (prev.size === 0) return prev;
      const currentIds = new Set(accumulated.map((i) => String(i.id)));
      const next = new Set<string>();
      for (const id of prev) {
        if (currentIds.has(id)) next.add(id);
      }
      return next;
    });
  }, [accumulated]);

  const hasNext = query.data?.hasNext ?? false;
  const isFetching = query.isFetching;
  loadMoreGateRef.current = { hasNext, isFetching };

  React.useEffect(() => {
    if (!active) return;
    const root = scrollRootRef?.current ?? internalScrollEl;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting);
        const gate = loadMoreGateRef.current;
        if (!hit || !gate.hasNext || gate.isFetching) return;
        setPage((p) => p + 1);
      },
      { root, rootMargin: '320px', threshold: 0 },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [active, accumulated.length, hasNext, scrollRootRef, internalScrollEl]);

  const masonryBlock = (
    <>
      <Masonry
        key={masonryColumnCount}
        breakpointCols={masonryColumnCount}
        className="asset-selector-masonry-grid"
        columnClassName="asset-selector-masonry-grid_column"
      >
        {query.isFetching && accumulated.length === 0
          ? Array.from({ length: GALLERY_PAGE_SIZE }).map((_, idx) => (
              <div
                key={`gallery-skeleton-${idx}`}
                className="mb-4 w-full overflow-hidden rounded-lg border border-border bg-muted"
                aria-hidden
              >
                <div
                  className="relative min-h-32 w-full animate-pulse bg-muted/50"
                  style={checkerboardStyle}
                />
              </div>
            ))
          : accumulated.map((item) => {
              const id = String(item.id);
              const src = (item.url ?? '').trim();
              const titleLabel =
                typeof item.name === 'string' && item.name.trim() ? item.name.trim() : '未命名';
              const loaded = loadedImageIds.has(id);
              const isSelected = selectedIds.has(id);

              return (
                <div
                  key={id}
                  className="group relative mb-4 w-full overflow-hidden rounded-lg border border-border bg-muted text-left transition-shadow hover:shadow-md"
                >
                  <button
                    type="button"
                    className="relative block w-full text-left"
                    onClick={() => {
                      if (!selectionMode || !src) return;
                      onToggleSelect(item);
                    }}
                    disabled={selectionMode && !src}
                  >
                    <div className="relative min-h-24 w-full overflow-hidden" style={checkerboardStyle}>
                      {src ? (
                        <>
                          <img
                            src={src}
                            alt={titleLabel}
                            className="block h-auto w-full object-contain transition-opacity duration-200"
                            style={{ opacity: loaded ? 1 : 0 }}
                            loading="lazy"
                            onLoad={() =>
                              setLoadedImageIds((prev) => {
                                if (prev.has(id)) return prev;
                                const next = new Set(prev);
                                next.add(id);
                                return next;
                              })
                            }
                            onError={() =>
                              setLoadedImageIds((prev) => {
                                if (prev.has(id)) return prev;
                                const next = new Set(prev);
                                next.add(id);
                                return next;
                              })
                            }
                          />
                          {!loaded ? (
                            <div
                              className="pointer-events-none absolute inset-0 animate-pulse bg-muted/45"
                              aria-hidden
                            />
                          ) : null}
                        </>
                      ) : (
                        <div className="h-24 w-full" />
                      )}
                      {!selectionMode ? (
                        <div
                          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 translate-y-full bg-invert/80 px-2.5 py-2 text-left text-xs font-medium text-invert-foreground transition-transform duration-200 ease-out group-hover:translate-y-0 group-focus-within:translate-y-0"
                          aria-hidden
                        >
                          <span className="line-clamp-2">{titleLabel}</span>
                        </div>
                      ) : null}
                    </div>
                  </button>
                  {selectionMode ? (
                    <div
                      className="absolute end-2 top-2 z-20"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        isSelected={isSelected}
                        isDisabled={!src}
                        onChange={() => onToggleSelect(item)}
                        aria-label={`选择 ${titleLabel}`}
                      >
                        <Checkbox.Control className="border-primary bg-background data-[selected=true]:border-primary data-[selected=true]:bg-primary">
                          <Checkbox.Indicator className="text-primary-foreground" />
                        </Checkbox.Control>
                      </Checkbox>
                    </div>
                  ) : null}
                </div>
              );
            })}
      </Masonry>
      {accumulated.length > 0 ? <div ref={loadMoreSentinelRef} className="h-6" aria-hidden /> : null}
      {!hasNext && accumulated.length > 0 ? (
        <p className="py-2 text-center text-xs text-muted-foreground">已加载全部</p>
      ) : null}
      {!query.isFetching && accumulated.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">暂无图片</p>
      ) : null}
    </>
  );

  return (
    <>
      <style>{`
        .asset-selector-masonry-grid { display: flex; margin-left: -1rem; width: auto; }
        .asset-selector-masonry-grid_column { padding-left: 1rem; background-clip: padding-box; }
      `}</style>
      {scrollRootRef ? (
        <div ref={measureRef} className="w-full min-w-0">
          {masonryBlock}
        </div>
      ) : (
        <div ref={setInternalScrollEl} className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-2">
          <div ref={measureRef} className="w-full min-w-0">
            {masonryBlock}
          </div>
        </div>
      )}
    </>
  );
}
